import { firebaseAuthService } from "@/core/shared/services/Auth/FirebaseAuthService";

import { AuthErrorCodes } from "@firebase/auth";
import { FirebaseError } from "@firebase/util";
import type {
  Auth,
  AuthProvider,
  OAuthCredential,
  UserCredential,
} from "firebase/auth";
import {
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";

export interface SocialAuthData {
  token: string;
  type: string;
}

export class AccountExistsFirebaseError extends FirebaseError {
  constructor(
    code: string,
    message: string,
    customData?: Record<string, unknown> | undefined
  ) {
    super(code, message, customData);
    Object.setPrototypeOf(this, AccountExistsFirebaseError.prototype);
  }
}

export default class SocialAuthService {
  static google(credential?: OAuthCredential): Promise<SocialAuthData> {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    return this.getAccessToken(
      firebaseAuthService,
      provider,
      "google",
      credential
    );
  }

  static facebook(credential?: OAuthCredential): Promise<SocialAuthData> {
    const provider = new FacebookAuthProvider();
    return this.getAccessToken(
      firebaseAuthService,
      provider,
      "facebook",
      credential
    );
  }

  static apple(credential?: OAuthCredential): Promise<SocialAuthData> {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("name");
    provider.addScope("email");
    return this.getAccessToken(
      firebaseAuthService,
      provider,
      "apple",
      credential
    );
  }

  static getAccessToken(
    auth: Auth,
    provider: AuthProvider,
    type: string,
    credential?: OAuthCredential
  ): Promise<SocialAuthData> {
    return new Promise<SocialAuthData>((resolve, reject) => {
      signInWithPopup(auth, provider)
        .then(async (response: UserCredential) => {
          if (credential && Object.keys(credential).length > 0) {
            await linkWithCredential(response.user, credential);
          }
          resolve({
            token: Reflect.get(response.user, "accessToken"),
            type: type,
          });
        })
        .catch(async (error) => {
          if (error.code === AuthErrorCodes.NEED_CONFIRMATION) {
            const providers = await fetchSignInMethodsForEmail(
              firebaseAuthService,
              error.customData.email
            );

            if (providers[0] === GoogleAuthProvider.PROVIDER_ID) {
              error.customData.existingProvider = "Google";
            } else if (providers[0] === FacebookAuthProvider.PROVIDER_ID) {
              error.customData.existingProvider = "Facebook";
            } else if (providers[0] === "apple.com") {
              error.customData.existingProvider = "Apple";
            }

            if (provider.providerId === GoogleAuthProvider.PROVIDER_ID) {
              error.customData.newProvider = "Google";
              error.customData.credential =
                GoogleAuthProvider.credentialFromError(error);
            } else if (
              provider.providerId === FacebookAuthProvider.PROVIDER_ID
            ) {
              error.customData.newProvider = "Facebook";
              error.customData.credential =
                FacebookAuthProvider.credentialFromError(error);
            } else if (provider.providerId === "apple.com") {
              error.customData.newProvider = "Apple";
              error.customData.credential =
                OAuthProvider.credentialFromError(error);
            }

            reject(
              new AccountExistsFirebaseError(
                error.code,
                error.message,
                error.customData
              )
            );
          }

          reject(error);
        });
    });
  }
}
